import type {
  CandidateOffer,
  CollegeDashboard,
  DriveEligibilitySummary,
  DriveRegistrationRow,
  DriveRegistrationStatus,
  OptOutReason,
  PlacementDriveDetail,
  PlacementAnalytics,
  PlacementOptOut,
  RoundResult,
  StudentDriveView,
} from '@welynk/types';
import { get, post, patch, put, del } from './http';
import { API_ENDPOINTS } from '../api-endpoints';
import type { PaginatedResponse } from '../api-types';

// ── Campus placement ────────────────────────────────────────────────────────

export interface DriveUpdateInput {
  title?: string;
  description?: string | null;
  externalCompanyName?: string | null;
  driveDate?: string | null;
  registrationDeadline?: string | null;
  status?: PlacementDriveDetail['status'];
  minCGPA?: number | null;
  backlogsAllowed?: boolean;
  allowedDepartmentIds?: string[];
  allowedBatchIds?: string[];
  targetGraduationYears?: number[];
  allowPlacedStudents?: boolean;
}

export class CampusPlacementApiService {
  // Student
  static myDrives() {
    return get<StudentDriveView[]>(API_ENDPOINTS.CAMPUS.MY_DRIVES);
  }
  static myDrive(driveId: string) {
    return get<StudentDriveView>(API_ENDPOINTS.CAMPUS.MY_DRIVE(driveId));
  }
  static register(
    driveId: string,
    data: { jobRoleId?: string | null; resumeId?: string | null } = {}
  ) {
    return post<StudentDriveView>(API_ENDPOINTS.CAMPUS.REGISTER(driveId), data);
  }
  static withdraw(driveId: string) {
    return post(API_ENDPOINTS.CAMPUS.WITHDRAW(driveId));
  }
  static myOffers() {
    return get<CandidateOffer[]>(API_ENDPOINTS.CAMPUS.MY_OFFERS);
  }
  static respondOffer(offerId: string, decision: 'ACCEPT' | 'DECLINE', reason?: string) {
    return post<CandidateOffer>(API_ENDPOINTS.CAMPUS.RESPOND_OFFER(offerId), { decision, reason });
  }
  static myOptOuts() {
    return get<PlacementOptOut[]>(API_ENDPOINTS.CAMPUS.MY_OPT_OUTS);
  }
  static requestOptOut(orgId: string, data: { reason: OptOutReason; details?: string }) {
    return post<PlacementOptOut>(API_ENDPOINTS.CAMPUS.REQUEST_OPT_OUT(orgId), data);
  }
  static withdrawOptOut(id: string) {
    return del(API_ENDPOINTS.CAMPUS.WITHDRAW_OPT_OUT(id));
  }
  /** Ask the college to lift an approved opt-out. */
  static requestRejoin(id: string) {
    return post<PlacementOptOut>(API_ENDPOINTS.CAMPUS.REQUEST_REJOIN(id));
  }

  // Placement cell
  static drive(orgId: string, driveId: string) {
    return get<PlacementDriveDetail>(API_ENDPOINTS.PLACEMENT_DRIVES.GET(orgId, driveId));
  }
  static updateDrive(orgId: string, driveId: string, data: DriveUpdateInput) {
    return patch<PlacementDriveDetail>(API_ENDPOINTS.PLACEMENT_DRIVES.UPDATE(orgId, driveId), data);
  }
  static setRounds(orgId: string, driveId: string, rounds: Array<{ id?: string; name: string }>) {
    return put<PlacementDriveDetail>(API_ENDPOINTS.PLACEMENT_DRIVES.ROUNDS(orgId, driveId), {
      rounds,
    });
  }
  static registrations(
    orgId: string,
    driveId: string,
    params: {
      status?: DriveRegistrationStatus;
      roundId?: string;
      search?: string;
      page?: number;
      pageSize?: number;
    } = {}
  ) {
    return get<PaginatedResponse<DriveRegistrationRow> & { counts: Record<string, number> }>(
      API_ENDPOINTS.PLACEMENT_DRIVES.REGISTRATIONS(orgId, driveId),
      params
    );
  }
  static recordRoundResults(
    orgId: string,
    driveId: string,
    data: {
      registrationIds: string[];
      roundId: string;
      result: RoundResult;
      note?: string;
      /** Fix an earlier result rather than record the student's current round. */
      correction?: boolean;
    }
  ) {
    return post<{ updated: number; skipped: Array<{ registrationId: string; reason: string }> }>(
      API_ENDPOINTS.PLACEMENT_DRIVES.ROUND_RESULTS(orgId, driveId),
      data
    );
  }
  static recordOffer(
    orgId: string,
    driveId: string,
    registrationId: string,
    data: {
      roleTitle?: string | null;
      ctcLpa?: number | null;
      companyName?: string | null;
      expiresAt?: string | null;
    }
  ) {
    return post(API_ENDPOINTS.PLACEMENT_DRIVES.RECORD_OFFER(orgId, driveId, registrationId), data);
  }
  static eligibilitySummary(orgId: string, driveId: string) {
    return get<DriveEligibilitySummary>(
      API_ENDPOINTS.PLACEMENT_DRIVES.ELIGIBILITY_SUMMARY(orgId, driveId)
    );
  }
  /** NEEDS_REVIEW = pending opt-outs plus open requests to opt back in. */
  static orgOptOuts(orgId: string, status?: 'PENDING' | 'APPROVED' | 'REJECTED' | 'NEEDS_REVIEW') {
    return get<PlacementOptOut[]>(API_ENDPOINTS.CAMPUS.ORG_OPT_OUTS(orgId), { status });
  }
  static reviewOptOut(orgId: string, id: string, decision: 'APPROVE' | 'REJECT', note?: string) {
    return post<PlacementOptOut>(API_ENDPOINTS.CAMPUS.REVIEW_OPT_OUT(orgId, id), {
      decision,
      note,
    });
  }
  /** Opt a student back in (answering their request, or on the cell's own call), or decline. */
  static reviewRejoin(orgId: string, id: string, decision: 'APPROVE' | 'REJECT', note?: string) {
    return post<PlacementOptOut>(API_ENDPOINTS.CAMPUS.REVIEW_REJOIN(orgId, id), { decision, note });
  }
  static correctPlacement(
    orgId: string,
    userId: string,
    data: {
      status: 'PLACED' | 'NOT_PLACED';
      companyName?: string | null;
      packageLpa?: number | null;
      reason: string;
    }
  ) {
    return post(API_ENDPOINTS.CAMPUS.CORRECT_PLACEMENT(orgId, userId), data);
  }
  static dashboard(orgId: string, departmentId?: string) {
    return get<CollegeDashboard>(API_ENDPOINTS.CAMPUS.DASHBOARD(orgId), { departmentId });
  }
  static analytics(orgId: string, graduationYear?: number) {
    return get<PlacementAnalytics>(API_ENDPOINTS.CAMPUS.ANALYTICS(orgId), { graduationYear });
  }
}
